import { ContactReferrerArrangement } from '@features/contacts/models';

import { OpportunitySchema } from '../modules/core/models/schema-property-definition';
import { CreditAdviceFeeType } from '@shared/components/credit-advice-fee/models/credit-advice-fee';

export namespace OpportunityReferrerArrangement {
  export interface ContactSchemaPropertyDefinition {
    id: OpportunitySchema.StringPropertyDefinition;
    type: OpportunitySchema.StringPropertyDefinition;
  }
  export interface SchemaPropertyDefinition {
    payee: {
      name: OpportunitySchema.StringPropertyDefinition;
      contact: ContactSchemaPropertyDefinition;
    };
    upfront: {
      amount: OpportunitySchema.NumberPropertyDefinition;
      percent: OpportunitySchema.NumberPropertyDefinition;
    };
    trail: OpportunitySchema.NumberPropertyDefinition;
  }

  export interface SchemaContract {
    payee: {
      name: string | null;
      affiliateId?: string | null;
      contact: {
        id: string | null;
        type: string | null;
      } | null;
    };
    upfront?:
      | {
          amount: number | null;
        }
      | {
          percent: number | null;
        }
      | null;
    trail?: number | null;
  }

  export interface Model {
    payee: {
      name: string | null;
      nmbAffiliateId?: string | null;
      contact: {
        id: string | null;
        type: string | null;
      } | null;
    };
    upfront: {
      type: ContactReferrerArrangement.UpfrontType | null;
      amount: number | null;
      percent: number | null;
    };
    trail: number | null;
  }

  export function toModel(value?: SchemaContract | null): Model | null {
    if (!value?.payee) {
      return null;
    }
    return {
      payee: {
        name: value.payee.name,
        nmbAffiliateId: value.payee.affiliateId || null,
        contact: {
          id: value.payee.contact?.id || null,
          type: value.payee.contact?.type || null
        }
      },
      upfront: value.upfront
        ? {
            type:
              'amount' in value.upfront
                ? ContactReferrerArrangement.UpfrontType.Amount
                : ContactReferrerArrangement.UpfrontType.Percent,
            amount: 'amount' in value.upfront ? value.upfront.amount : null,
            percent: 'percent' in value.upfront ? value.upfront.percent : null
          }
        : {
            type: null,
            amount: null,
            percent: null
          },
      trail: value.trail || null
    };
  }

  export function toSchemaContract(
    value: Model | null | undefined
  ): SchemaContract | null | undefined {
    if (value) {
      return {
        payee: {
          name: value.payee.name,
          affiliateId: value.payee.nmbAffiliateId,
          contact: value.payee.contact
        },
        upfront:
          value.upfront.type === ContactReferrerArrangement.UpfrontType.Amount
            ? {
                amount: value.upfront.amount
              }
            : value.upfront.type ===
              ContactReferrerArrangement.UpfrontType.Percent
            ? {
                percent: value.upfront.percent
              }
            : null,
        trail: value.trail
      };
    }

    return value;
  }
}

export namespace OpportunitySummary {
  export interface ReferrerSchemaPropertyDefinition {
    referrer: OpportunitySchema.StringPropertyDefinition;
    arrangement: OpportunityReferrerArrangement.SchemaPropertyDefinition;
  }

  export type ChargedAdviceFeeDefinition = {
    feeType: OpportunitySchema.OptionsPropertyDefinition;
    amount: OpportunitySchema.NumberPropertyDefinition;
  }

  export type WaivedAdviceFeeDefinition = {
    amount: OpportunitySchema.NumberPropertyDefinition;
  }

  export type AdviceFeeDefinition = {
    charged: ChargedAdviceFeeDefinition;
    waived: WaivedAdviceFeeDefinition;
  };

  export interface SchemaPropertyDefinition {
    name: OpportunitySchema.StringPropertyDefinition;
    estimatedAmount: OpportunitySchema.NumberPropertyDefinition;
    adviceFee: AdviceFeeDefinition;
    marketingSource: OpportunitySchema.OptionsPropertyDefinition;
    referrerArrangement: ReferrerSchemaPropertyDefinition;
  }

  export type AdviceFeeContract = {
    charged?: {
      feeType: CreditAdviceFeeType;
      amount: number;
    };
    waived?: {
      amount?: number | null;
    };
  };

  export interface ReferrerArrangementSchemaContract {
    payeeName?: string | null;
    upfront?:
      | {
          amount: number | null;
        }
      | {
          percent: number | null;
        }
      | null;
    trail?: number | null;
  }

  export type SchemaContract = {
    name?: string;
    estimatedAmount?: number | null;
    adviceFee?: AdviceFeeContract | null;
    marketingSource?: string | null;
    adviserCampaignId?: string | null;
    referrerArrangement?: {
      referrer?: ContactReferrerArrangement.Type | null;
      arrangement?: OpportunityReferrerArrangement.SchemaContract | null;
    };
  };

  export interface ReferrerArrangementModel {
    payeeName: string | null;
    upfront: {
      type: ContactReferrerArrangement.UpfrontType | null;
      amount: number | null;
      percent: number | null;
    };
    trail: number | null;
  }

  export type Model = {
    name: string | null;
    estimatedAmount: number | null;
    waiveAdviceFee: { amount: number | null; } | null;
    creditAdviceFee: { feeType: CreditAdviceFeeType; amount: number; } | null;
    marketingSource: string | null;
    adviserCampaignId: string | null;
    referrer: ContactReferrerArrangement.Type | null;
    arrangement: OpportunityReferrerArrangement.Model | null;
  };

  export function toModel(value: SchemaContract): Model {
    let waiveAdviceFee: { amount: number | null } | null = null;
    let creditAdviceFee: { feeType: CreditAdviceFeeType; amount: number; } | null = null;
    const adviceFeeValue = value.adviceFee;

    if (typeof adviceFeeValue === 'object' && adviceFeeValue !== null) {
      if (adviceFeeValue.waived) {
        waiveAdviceFee = { amount: adviceFeeValue.waived?.amount ?? null };
      } else if (adviceFeeValue.charged) {
        waiveAdviceFee = null;
        creditAdviceFee = {
          feeType: adviceFeeValue.charged.feeType,
          amount: adviceFeeValue.charged.amount
        };
      }
    }

    return {
      name: value.name ?? null,
      estimatedAmount: value.estimatedAmount ?? null,
      waiveAdviceFee: waiveAdviceFee,
      creditAdviceFee: creditAdviceFee,
      marketingSource: value.marketingSource ?? null,
      adviserCampaignId: value.adviserCampaignId ?? null,
      referrer: value.referrerArrangement?.referrer ?? null,
      arrangement: OpportunityReferrerArrangement.toModel(
        value.referrerArrangement?.arrangement
      )
    };
  }

  export function toSchemaContract(value: Partial<Model>): SchemaContract {
    let adviceFee: AdviceFeeContract | undefined = undefined;

    adviceFee = value.waiveAdviceFee
        ? { waived: { amount: value.waiveAdviceFee.amount } }
        : value.creditAdviceFee
          ? {
              charged: {
                feeType: value.creditAdviceFee.feeType,
                amount: value.creditAdviceFee.amount
              }
            }
          : undefined;

    return {
      name: value.name?.trim(),
      estimatedAmount: value.estimatedAmount,
      adviceFee: adviceFee,
      marketingSource: value.marketingSource,
      adviserCampaignId: value.adviserCampaignId,
      referrerArrangement: {
        referrer: value.referrer,
        arrangement: OpportunityReferrerArrangement.toSchemaContract(
          value.arrangement
        )
      }
    };
  }
}
